'use client';

import { Stack } from '@mantine/core';
import {
  IconApi,
  IconMessage,
  IconPhone,
  IconPencilPlus,
  IconSettings,
  IconLogout,
  IconRobot,
  IconBuildingStore,
  IconShieldCheck,
  IconMessageCircle,
} from '@tabler/icons-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { IconLogin } from '@tabler/icons-react';

import { useAuthStore } from '@/stores/authStore';
import { useAppModeStore } from '@/stores/appModeStore';
import { useAuthGateStore } from '@/stores/authGateStore';
import { useHomePath } from '@/hooks/useHomePath';
import { LogoMark } from '@/components/LogoMark';
import { SidebarLink } from './SidebarLink';
import classes from './Sidebar.module.css';

interface SidebarProps {
  collapsed: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function Sidebar({ collapsed, onMouseEnter, onMouseLeave }: SidebarProps) {
  const router = useRouter();
  const logout = useAuthStore((s) => s.logout);
  const sessionToken = useAuthStore((s) => s.sessionToken);
  const aurigaUid = useAuthStore((s) => s.aurigaUid);
  const isAuthed = Boolean(sessionToken && aurigaUid);
  const { path: homePath } = useHomePath();
  const isAppMode = useAppModeStore((s) => s.isAppMode);
  const appConfig = useAppModeStore((s) => s.appConfig);
  const openAuthGate = useAuthGateStore((s) => s.open);

  const handleLogout = () => {
    logout();
    router.push('/login');
  };

  const logoElement = isAppMode && appConfig?.app_icon_url ? (
    <Image src={appConfig.app_icon_url} alt="" width={24} height={24} unoptimized />
  ) : (
    <LogoMark size={24} />
  );

  const logoText = isAppMode && appConfig?.display_name
    ? appConfig.display_name
    : 'ReadyLoop';

  return (
    <nav
      className={classes.sidebar}
      data-collapsed={collapsed || undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={classes.header}>
        <Link href={homePath} className={classes.logoLink}>
          {logoElement}
          <span
            className={classes.logoText}
            data-collapsed={collapsed || undefined}
          >
            {logoText}
          </span>
        </Link>
      </div>

      <Stack gap={6} className={classes.links}>
        {isAppMode ? (
          <>
            <SidebarLink
              icon={IconMessage}
              label="Chat"
              href="/chat"
              collapsed={collapsed}
            />
            {!appConfig?.voice_disabled && (
              <SidebarLink
                icon={IconPhone}
                label="Voice"
                href="/voice"
                collapsed={collapsed}
              />
            )}
          </>
        ) : (
          <>
            <SidebarLink
              icon={IconPencilPlus}
              label="Build"
              href="/build"
              collapsed={collapsed}
            />
            <SidebarLink
              icon={IconRobot}
              label="Agents"
              href="/agents"
              collapsed={collapsed}
            />
            <SidebarLink
              icon={IconBuildingStore}
              label="Marketplace"
              href="/marketplace"
              collapsed={collapsed}
            />
            <SidebarLink
              icon={IconShieldCheck}
              label="Governance"
              href="/governance"
              collapsed={collapsed}
            />
          </>
        )}
      </Stack>

      <Stack gap={6} className={classes.footer}>
        {isAuthed && (
          <SidebarLink
            icon={IconSettings}
            label="Settings"
            href="/settings"
            collapsed={collapsed}
          />
        )}
        {!isAppMode && (
          <SidebarLink
            icon={IconApi}
            label="Docs"
            href="/docs"
            collapsed={collapsed}
          />
        )}
        {!isAppMode && (
          <SidebarLink
            icon={IconMessageCircle}
            label="Support"
            href="https://readyloop.ai/contact"
            external
            collapsed={collapsed}
          />
        )}
        {isAuthed ? (
          <SidebarLink
            icon={IconLogout}
            label="Logout"
            onClick={handleLogout}
            collapsed={collapsed}
            color="var(--rl-danger)"
          />
        ) : (
          <SidebarLink
            icon={IconLogin}
            label="Sign in"
            onClick={openAuthGate}
            collapsed={collapsed}
          />
        )}
      </Stack>
    </nav>
  );
}
